import React from "react";
import LegalLayout, { H2, H3, P, UL, LI, CALLOUT } from "../components/LegalLayout";
import { ShieldIllustration } from "../components/legal/LegalIllustrations";

const SECTIONS = [
  { id: "verification", labelFr: "Vérification", labelEn: "Verification" },
  { id: "allowed", labelFr: "Ce qui est autorisé", labelEn: "What's allowed" },
  { id: "not-allowed", labelFr: "Ce qui ne l'est pas", labelEn: "What isn't" },
  { id: "activities", labelFr: "Activités entre membres", labelEn: "Activities between members" },
  { id: "tools", labelFr: "Bloquer, masquer, signaler", labelEn: "Block, hide, report" },
  { id: "safety", labelFr: "Signaux de sécurité", labelEn: "Safety signals" },
  { id: "consequences", labelFr: "Conséquences et recours", labelEn: "Consequences and appeals" },
  { id: "children", labelFr: "Mineurs", labelEn: "Minors" },
];

export default function CommunityRules() {
  return (
    <LegalLayout
      titleFr="Règles de la communauté" titleEn="Community Rules"
      taglineFr="Ce qui est autorisé sur OLTODO, ce qui ne l'est pas, et comment on protège la communauté."
      taglineEn="What's allowed on OLTODO, what isn't, and how we protect the community."
      updatedAt="11 septembre 2026"
      illustration={<ShieldIllustration />}
      sections={SECTIONS}
      tldrFr={[
        "Un selfie est obligatoire avant de pouvoir participer, créer ou contacter qui que ce soit. Une pièce justificative reste facultative.",
        "Bloquer coupe tout contact, masquer cache juste le contenu de quelqu'un sans qu'il le sache, signaler alerte l'équipe OLTODO.",
        "Toute violation peut entraîner un avertissement, une restriction, une suspension ou une suppression du compte, selon la gravité.",
        "Toute décision de modération peut être contestée par email, une vraie personne la réexamine.",
      ]}
      tldrEn={[
        "A selfie is required before you can join, create, or contact anyone. A supporting document remains optional.",
        "Blocking cuts all contact, hiding just removes someone's content without them knowing, reporting alerts the OLTODO team.",
        "Any violation can lead to a warning, a restriction, a suspension, or account deletion, depending on severity.",
        "Every moderation decision can be challenged by email, a real person reviews it.",
      ]}
    >
      {(lang) => lang === "fr" ? (
        <>
          <P>
            OLTODO met en relation des personnes réelles pour partager des activités, des services et des moments
            de vie. Ces règles existent pour que cette mise en relation reste sûre, respectueuse et honnête, pour
            tout le monde. Elles s'appliquent à chaque profil, chaque publication, chaque message et chaque
            interaction sur l'application et le site.
          </P>

          <H2 id="verification">1. Vérification d'identité obligatoire</H2>
          <P>
            Contrairement à beaucoup de réseaux sociaux, OLTODO n'autorise personne à interagir sans avoir
            d'abord vérifié qui il est. Dès la création d'un compte, il est possible de naviguer et de consulter
            l'application, mais toute action qui implique une autre personne, participer à une activité, en
            créer une, envoyer un message, liker un profil, réserver une prestation, est bloquée tant que la
            vérification n'est pas faite.
          </P>
          <P>La vérification repose sur un selfie pris directement dans l'application, jamais importé depuis la
          galerie. Une pièce justificative peut s'y ajouter, à tout moment, mais reste entièrement facultative :</P>
          <UL>
            <LI>Obligatoire : un selfie de la personne qui crée le compte.</LI>
            <LI>Facultatif : une pièce justificative (pièce d'identité, passeport, permis de conduire, facture ou titre de transport), qui renforce la confiance des autres membres.</LI>
          </UL>
          <CALLOUT>
            Ces photos sont transmises uniquement à l'équipe de modération OLTODO pour vérifier le compte.
            Elles sont supprimées définitivement dès
            qu'une décision est prise, jamais conservées, jamais utilisées à une autre fin, jamais partagées
            avec d'autres membres ni avec un tiers.
          </CALLOUT>
          <P>
            Une demande refusée peut être soumise à nouveau avec de meilleures photos. Un compte dont l'identité
            ne peut pas être établie après plusieurs tentatives reste limité à la consultation.
          </P>

          <H2 id="allowed">2. Ce qui est encouragé</H2>
          <P>OLTODO existe pour que les gens se rencontrent, apprennent les uns des autres et s'entraident, autour
          de vraies passions et de vrais services :</P>
          <UL>
            <LI>Organiser ou rejoindre une activité (sortie, sport, atelier, covoiturage, garde d'enfants, etc.), seul ou en groupe.</LI>
            <LI>Proposer ou solliciter un service entre particuliers ou auprès d'un professionnel référencé.</LI>
            <LI>Partager une passion au sein d'une communauté ou d'un rituel régulier avec d'autres membres.</LI>
            <LI>Faire des rencontres, amicales ou romantiques, entre adultes consentants.</LI>
            <LI>Évaluer honnêtement une expérience une fois qu'elle a eu lieu.</LI>
          </UL>

          <H2 id="not-allowed">3. Ce qui n'est jamais autorisé</H2>
          <P>Les comportements et contenus suivants sont interdits sur OLTODO, quel que soit le format
          (profil, publication, photo, message, commentaire, avis, nom d'utilisateur) :</P>
          <H3>Contenu et sécurité</H3>
          <UL>
            <LI>Contenu illicite, violent, haineux, ou faisant l'apologie d'un comportement dangereux.</LI>
            <LI>Contenu à caractère sexuel non consenti, ou impliquant un mineur sous quelque forme que ce soit, y compris suggérée.</LI>
            <LI>Menaces, chantage, harcèlement, intimidation, ou incitation à la haine envers une personne ou un groupe.</LI>
            <LI>Contenu trompeur destiné à manipuler, arnaquer ou soutirer de l'argent à un autre membre.</LI>
          </UL>
          <H3>Authenticité</H3>
          <UL>
            <LI>Créer un faux profil, usurper l'identité d'un tiers, ou se faire passer pour quelqu'un d'autre.</LI>
            <LI>Utiliser des photos qui ne sont pas les siennes lors de la vérification d'identité.</LI>
            <LI>Détenir plusieurs comptes pour contourner un blocage, une suspension, ou une limite du service.</LI>
          </UL>
          <H3>Usage du service</H3>
          <UL>
            <LI>Démarchage commercial non sollicité, spam, ou promotion d'un service extérieur sans lien avec l'activité publiée.</LI>
            <LI>Extraction automatisée de données (scraping), ou toute tentative de contourner les mesures de sécurité du service.</LI>
            <LI>Revente ou sous-location d'une place, d'un accès, ou d'un service obtenu via OLTODO, sans l'accord de l'organisateur.</LI>
          </UL>

          <H2 id="activities">4. Activités et rencontres entre membres</H2>
          <P>
            Les activités publiées sur OLTODO (sorties, prestations, covoiturages, rituels, événements) sont
            organisées par les membres eux-mêmes. OLTODO fournit l'outil de mise en relation, la messagerie, le
            paiement le cas échéant, et un socle de confiance (vérification, avis, signalement), mais n'organise
            pas ces activités et n'est pas partie à ce qui s'y passe.
          </P>
          <P>
            Chaque participant reste seul responsable de son comportement, de la véracité des informations qu'il
            partage, et de sa sécurité personnelle. Nous recommandons, en particulier lors d'une première
            rencontre : privilégier un lieu public, prévenir un proche de l'heure et du lieu, et utiliser la
            messagerie de l'application plutôt qu'un numéro personnel tant que la confiance n'est pas établie.
          </P>
          <P>
            Après une activité, les participants peuvent s'évaluer mutuellement. Ces évaluations nourrissent le
            score social et les badges affichés sur un profil. Une évaluation manifestement abusive, hors sujet
            ou rédigée pour nuire peut être supprimée par l'équipe OLTODO, sur signalement ou à notre initiative.
          </P>

          <H2 id="tools">5. Bloquer, masquer et signaler</H2>
          <P>OLTODO distingue volontairement trois outils, pour trois situations différentes :</P>
          <H3>Bloquer</H3>
          <P>Coupe tout contact dans les deux sens, immédiatement : plus de messages possibles, plus de visibilité
          mutuelle des profils et publications, retrait automatique de toute relation d'amitié existante. La
          personne bloquée n'est jamais informée qu'elle l'a été.</P>
          <H3>Masquer</H3>
          <P>Ne fait disparaître le contenu de quelqu'un que de ton propre fil, sans le bloquer, sans qu'il en soit
          informé, et sans l'empêcher de te contacter. Utile pour ne plus voir quelqu'un que tu ne souhaites pas
          bloquer explicitement.</P>
          <H3>Signaler</H3>
          <P>Envoie un signalement à l'équipe de modération OLTODO, avec le motif choisi. Un signalement est
          toujours examiné par une personne, jamais uniquement par un algorithme. Signaler ne bloque ni ne masque
          automatiquement, ces trois actions sont indépendantes et peuvent être combinées.</P>

          <H2 id="safety">6. Signaux de sécurité dans les discussions</H2>
          <P>
            Quand tu ouvres une conversation privée avec quelqu'un pour la première fois, OLTODO affiche un
            avertissement si deux signaux se combinent : aucun ami en commun avec cette personne, et un compte
            créé depuis moins de 14 jours. Ce n'est jamais une accusation, seulement une invitation à la prudence,
            avec un accès direct à Bloquer et Signaler depuis ce même bandeau.
          </P>

          <H2 id="consequences">7. Conséquences et droit de recours</H2>
          <P>Selon la nature et la gravité d'une violation de ces règles, OLTODO peut :</P>
          <UL>
            <LI>Retirer ou masquer le contenu concerné.</LI>
            <LI>Adresser un avertissement au compte concerné.</LI>
            <LI>Restreindre temporairement certaines fonctionnalités (publication, messagerie, participation).</LI>
            <LI>Suspendre le compte pour une durée déterminée.</LI>
            <LI>Supprimer définitivement le compte, sans préavis en cas de manquement grave (sécurité d'autrui, fraude, contenu illégal).</LI>
          </UL>
          <P>
            Toute décision de modération peut être contestée en écrivant à <a href="mailto:contact@oltodo.com" style={{ color: "#0a0a0a", fontWeight: 700 }}>contact@oltodo.com</a>,
            en précisant le compte concerné et le motif de la contestation. Une personne de l'équipe OLTODO,
            distincte de celle ayant pris la décision initiale lorsque c'est possible, réexamine chaque demande.
          </P>

          <H2 id="children">8. Mineurs</H2>
          <P>
            OLTODO est réservé aux personnes âgées d'au moins 18 ans. La vérification d'identité (section 1)
            permet de contrôler cette condition à l'entrée. Tout compte dont il apparaît qu'il appartient à un
            mineur de moins de 18 ans est suspendu, et tout contenu impliquant un mineur de quelque façon que ce
            soit est retiré immédiatement et peut être signalé aux autorités compétentes.
          </P>
        </>
      ) : (
        <>
          <P>
            OLTODO connects real people to share activities, services and moments of everyday life. These rules
            exist so that this connection stays safe, respectful and honest, for everyone. They apply to every
            profile, post, photo, message, comment and interaction on the app and website.
          </P>

          <H2 id="verification">1. Mandatory Identity Verification</H2>
          <P>
            Unlike many social networks, OLTODO does not let anyone interact before verifying who they are. As
            soon as an account is created, browsing and viewing the app is possible, but any action involving
            another person, joining an activity, creating one, sending a message, liking a profile, booking a
            service, is blocked until verification is complete.
          </P>
          <P>Verification relies on a selfie taken directly in the app, never imported from the gallery. A supporting document can be added at any time but remains entirely optional:</P>
          <UL>
            <LI>Required: a selfie of the person creating the account.</LI>
            <LI>Optional: a supporting document (ID card, passport, driver's license, utility bill or transit pass), which builds trust with other members.</LI>
          </UL>
          <CALLOUT>
            These photos are sent only to the OLTODO moderation team to verify the account.
            They are permanently deleted as soon as a decision is made, never kept, never used for any other
            purpose, never shared with other members or third parties.
          </CALLOUT>
          <P>
            A rejected request can be resubmitted with better photos. An account whose identity cannot be
            established after several attempts remains limited to browsing.
          </P>

          <H2 id="allowed">2. What We Encourage</H2>
          <P>OLTODO exists so people can meet, learn from each other and help one another, around real passions
          and real services:</P>
          <UL>
            <LI>Organizing or joining an activity (outing, sport, workshop, carpooling, childcare, etc.), alone or in a group.</LI>
            <LI>Offering or requesting a service between individuals, or from a listed professional.</LI>
            <LI>Sharing a passion within a community or a recurring ritual with other members.</LI>
            <LI>Meeting people, as friends or romantically, between consenting adults.</LI>
            <LI>Honestly rating an experience once it has taken place.</LI>
          </UL>

          <H2 id="not-allowed">3. What's Never Allowed</H2>
          <P>The following behaviors and content are prohibited on OLTODO, regardless of format
          (profile, post, photo, message, comment, review, username):</P>
          <H3>Content and safety</H3>
          <UL>
            <LI>Unlawful, violent, hateful content, or content that glorifies dangerous behavior.</LI>
            <LI>Non-consensual sexual content, or content involving a minor in any form, including implied.</LI>
            <LI>Threats, blackmail, harassment, intimidation, or incitement to hatred against a person or group.</LI>
            <LI>Deceptive content meant to manipulate, scam or extract money from another member.</LI>
          </UL>
          <H3>Authenticity</H3>
          <UL>
            <LI>Creating a fake profile, impersonating a third party, or pretending to be someone else.</LI>
            <LI>Using photos that aren't your own during identity verification.</LI>
            <LI>Holding multiple accounts to bypass a block, a suspension, or a service limit.</LI>
          </UL>
          <H3>Use of the service</H3>
          <UL>
            <LI>Unsolicited commercial outreach, spam, or promoting an unrelated outside service.</LI>
            <LI>Automated data extraction (scraping), or attempting to bypass the service's security measures.</LI>
            <LI>Reselling or subletting a spot, an access, or a service obtained through OLTODO without the organizer's consent.</LI>
          </UL>

          <H2 id="activities">4. Activities and Meetups Between Members</H2>
          <P>
            Activities posted on OLTODO (outings, services, carpools, rituals, events) are organized by members
            themselves. OLTODO provides the matching tool, messaging, payment when applicable, and a foundation
            of trust (verification, reviews, reporting), but does not organize these activities and is not a
            party to what happens there.
          </P>
          <P>
            Each participant remains solely responsible for their behavior, the accuracy of the information they
            share, and their personal safety. We recommend, especially for a first meeting, choosing a public
            place, letting someone know the time and location, and using the app's messaging rather than a
            personal number until trust is established.
          </P>
          <P>
            After an activity, participants can rate each other. These ratings feed into the social score and
            badges shown on a profile. A rating that is clearly abusive, off-topic, or written to cause harm can
            be removed by the OLTODO team, whether reported or on our own initiative.
          </P>

          <H2 id="tools">5. Block, Hide and Report</H2>
          <P>OLTODO deliberately distinguishes three tools, for three different situations:</P>
          <H3>Block</H3>
          <P>Cuts all contact both ways, immediately: no more messages, no more mutual visibility of profiles and
          posts, automatic removal of any existing friendship. The blocked person is never told they were blocked.</P>
          <H3>Hide</H3>
          <P>Only removes someone's content from your own feed, without blocking them, without them knowing, and
          without preventing them from contacting you. Useful when you'd rather not see someone without
          explicitly blocking them.</P>
          <H3>Report</H3>
          <P>Sends a report to the OLTODO moderation team, with the chosen reason. A report is always reviewed by
          a person, never only by an algorithm. Reporting doesn't automatically block or hide, these three
          actions are independent and can be combined.</P>

          <H2 id="safety">6. Safety Signals in Conversations</H2>
          <P>
            When you open a private conversation with someone for the first time, OLTODO shows a warning if two
            signals combine: no mutual friends with that person, and an account created less than 14 days ago.
            This is never an accusation, just an invitation to be cautious, with direct access to Block and
            Report from that same banner.
          </P>

          <H2 id="consequences">7. Consequences and the Right to Appeal</H2>
          <P>Depending on the nature and severity of a violation of these rules, OLTODO may:</P>
          <UL>
            <LI>Remove or hide the content in question.</LI>
            <LI>Issue a warning to the account.</LI>
            <LI>Temporarily restrict certain features (posting, messaging, participation).</LI>
            <LI>Suspend the account for a set period.</LI>
            <LI>Permanently delete the account, without notice for serious misconduct (safety, fraud, illegal content).</LI>
          </UL>
          <P>
            Any moderation decision can be challenged by writing to <a href="mailto:contact@oltodo.com" style={{ color: "#0a0a0a", fontWeight: 700 }}>contact@oltodo.com</a>,
            specifying the account concerned and the reason for the appeal. A member of the OLTODO team, distinct
            from whoever made the original decision where possible, reviews each request.
          </P>

          <H2 id="children">8. Minors</H2>
          <P>
            OLTODO is reserved for people aged 18 and over. Identity verification (section 1) helps enforce this
            at entry. Any account found to belong to someone under 18 is suspended, and any content involving a
            minor in any way is removed immediately and may be reported to the relevant authorities.
          </P>
        </>
      )}
    </LegalLayout>
  );
}
